import { cn } from "@/lib/utils";
import { useQuestion } from "@/store/question.context";
import { CheckCircle, FileWarning } from "lucide-react";
import { motion } from "motion/react";
import { FC, useState } from "react";
import QuestionMetaItem from "./question-meta-item";

const badgeClassName =
  "rounded-full px-2 py-1 text-sm font-medium inline-flex items-center min-w-[32px] min-h-[32px] cursor-default";

type QuestionCardHeaderProps = {
  topicOrder: number;
  questionOrder: number;
};

const QuestionCardHeader: FC<QuestionCardHeaderProps> = ({ ...props }) => {
  const { question } = useQuestion();

  const [markHovered, setMarkHovered] = useState(false);

  if (!question) {
    return null;
  }

  return (
    <header>
      <div className="flex gap-2">
        <h4 className="grow text-lg font-bold text-[#293038]">
          {props.topicOrder}.{props.questionOrder}. {question.subtopic}
        </h4>

        <div className="min-w-[200px] text-right">
          {question.completed ? (
            <motion.div
              className={cn(badgeClassName, "bg-[#9DE7B9] text-[#1c6c3c]")}
              onHoverStart={() => setMarkHovered(true)}
              onHoverEnd={() => setMarkHovered(false)}
            >
              <CheckCircle size={16} />
              <motion.span
                initial={{ width: 0, opacity: 0 }}
                animate={
                  markHovered
                    ? { width: "auto", opacity: 1, marginLeft: "0.5rem" }
                    : { width: 0, opacity: 0 }
                }
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden whitespace-nowrap font-medium"
              >
                Pregunta respondida
              </motion.span>
            </motion.div>
          ) : (
            <motion.div
              className={cn(badgeClassName, "bg-gray-100 text-gray-500")}
              onHoverStart={() => setMarkHovered(true)}
              onHoverEnd={() => setMarkHovered(false)}
            >
              <FileWarning size={16} />
              <motion.span
                initial={{ width: 0, opacity: 0 }}
                animate={
                  markHovered
                    ? { width: "auto", opacity: 1, marginLeft: "0.5rem" }
                    : { width: 0, opacity: 0 }
                }
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden whitespace-nowrap font-medium"
              >
                Necesita una respuesta
              </motion.span>
            </motion.div>
          )}
        </div>
      </div>

      {(question.purpose || question.calculation || question.source) && (
        <div className="mt-4 flex flex-col gap-4 text-sm font-medium text-[#8090A3]">
          {question.purpose && (
            <QuestionMetaItem
              title="Propósito de la pregunta"
              description={question.purpose}
            />
          )}

          {question.calculation && (
            <QuestionMetaItem
              title="Requerimiento esperado"
              description={question.calculation}
            />
          )}

          {question.source && (
            <QuestionMetaItem title="Fuente" description={question.source} />
          )}
        </div>
      )}
    </header>
  );
};

export default QuestionCardHeader;
